'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { Logout } from '@mui/icons-material';
import { KeyboardArrowDown } from '@mui/icons-material';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import { isFalsy } from 'utility-types';
import { getUserStores } from '@/api/appuser.api';
import { getTeamInfobyOgranizationId } from '@/api/organization.api';
import { useAppStoreStore } from '@/stores/appstore.store';
import { useFavoriteStore } from '@/stores/favoriteStore.store';
import { useOrganizationTeamStore } from '@/stores/organizationTeam.store';
import { ROUTES } from '@/constants/path.constants';
import { getToken } from '@/utils/auth.util';
import { getUserInfo } from '@/utils/user.util';
import FlexBox from '@/components/FlexBox';
import HospitalMenu from '@/components/HospitalMenu';
import { useNavigationLogic } from '@/hooks/useNavigationLogic';
import { HeaderLogo } from '@/shared/ui/atoms';
import RoundedButton from '@/shared/ui/atoms/RoundedButton';
import DesktopNavigations from './DesktopNavigations';

const DesktopHeader = () => {
  const router = useRouter();
  const appUserName = getUserInfo()?.appUserName ?? '';
  const [IsUserLoggedIn, setIsUserLoggedIn] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);
  const { setAppUserStoreInfo } = useFavoriteStore();
  const { setTeams, setOrganizations } = useOrganizationTeamStore();
  const { appStores } = useAppStoreStore((state) => state);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // appStores가 객체일 때 첫 번째 store를 찾아서 사용
  const firstStore = Object.values(appStores)[0];
  // const userInfo = getUserInfo();

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    // 클라이언트 하이드레이션 완료 표시 (SSR 깜빡임 방지)
    setIsHydrated(true);

    const checkToken = async () => {
      const token = getToken();
      const isLoggedIn = !isFalsy(token?.accessToken);
      setIsUserLoggedIn(isLoggedIn);

      if (isLoggedIn) {
        try {
          const storesResult = await getUserStores();
          if (!storesResult.isSuccess || !storesResult.data) {
            return;
          }

          if (storesResult.data.organizationAppUserInfo?.[0]?.status === 'TEMPORARY') {
            return;
          }

          const { appUserStoreInfo, organizationAppUserInfo } = storesResult.data;
          if (appUserStoreInfo) {
            setAppUserStoreInfo(appUserStoreInfo);
          }

          if (organizationAppUserInfo?.length === 0) {
            return;
          }

          if (storesResult.data.organizationAppUserInfo?.[0]?.status !== 'TEMPORARY' && organizationAppUserInfo) {
            setOrganizations(organizationAppUserInfo);
            setTeams(organizationAppUserInfo[0]?.teamAppUserStoresInfo ?? []);

            if (organizationAppUserInfo[0]?.organizationRole !== 'ORGANIZATION_USER') {
              const teamResult = await getTeamInfobyOgranizationId(organizationAppUserInfo[0].organizationId);
              if (!teamResult.isSuccess || !teamResult.data) {
                return;
              } else {
                setTeams(teamResult.data);
              }
            }
          }
        } catch (error) {
          console.error('병원 정보 조회 중 오류:', error);
        }
      }
    };
    checkToken();
  }, []);

  const { signOut } = useNavigationLogic();

  return (
    <div className="w-full flex items-center justify-between">
      <FlexBox style={{ gap: '40px' }}>
        <HeaderLogo IsUserLoggedIn={IsUserLoggedIn} />
        <DesktopNavigations />
      </FlexBox>
      {/* 하이드레이션 전에는 사용자 영역을 렌더하지 않아 깜빡임 방지 */}
      {!isHydrated ? null : IsUserLoggedIn ? (
        <FlexBox style={{ gap: '24px' }}>
          {firstStore?.storeName && (
            <Box
              onClick={handleMenuClick}
              sx={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '4px 8px',
                borderRadius: '4px',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)'
                }
              }}
            >
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  maxWidth: '200px'
                }}
              >
                {firstStore?.storeName}
              </Typography>
              <KeyboardArrowDown
                sx={{
                  fontSize: '20px',
                  color: 'text.secondary',
                  transition: 'transform 0.2s',
                  transform: anchorEl ? 'rotate(180deg)' : 'none'
                }}
              />
            </Box>
          )}
          <FlexBox style={{ gap: '12px' }}>
            <Typography
              variant="subtitle2"
              color="text"
              sx={{
                flex: 1,
                maxWidth: '200px',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                overflow: 'hidden'
              }}
            >
              {appUserName}
            </Typography>
            <IconButton onClick={signOut} sx={{ padding: 0, width: '24px' }} aria-label="로그아웃">
              <Logout />
            </IconButton>
          </FlexBox>
        </FlexBox>
      ) : (
        <div className="flex gap-4">
          <RoundedButton
            className="py-[7px] px-[14px] text-[#FFFFFF] text-sm font-semibold leading-[22px] bg-[#6B00D7] rounded-[8px]"
            label="회원가입"
            onClick={() => router.push(ROUTES.getSignUp())}
          />
          <RoundedButton
            className="py-[7px] px-[14px] text-[#1F2937] text-sm font-semibold leading-[22px] bg-[#FFFFFF] border-[1px] border-[#E5E7EB] rounded-[8px]"
            label="로그인"
            onClick={() => router.push(ROUTES.getLogin())}
          />
        </div>
      )}
      <HospitalMenu anchorEl={anchorEl} onClose={handleMenuClose} />
    </div>
  );
};

export default DesktopHeader;
